import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import { INBOX_TYPES } from 'shared/mixins/inboxMixin';
import InboxesAPI from '../../api/inboxes';
import WebChannel from '../../api/channel/webChannel';
import FBChannel from '../../api/channel/fbChannel';
import TwilioChannel from '../../api/channel/twilioChannel';
import WhatsappChannel from '../../api/channel/whatsappChannel';
import { throwErrorMessage } from '../utils/api';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { ACCOUNT_EVENTS } from '../../helper/AnalyticsHelper/events';
import Voll360 from '../../api/voll360';
import Wetalkie from '../../api/wetalkie';
import Zendesk from '../../api/zendesk';
import Infobip from '../../api/infobip';

const buildInboxData = inboxParams => {
  const formData = new FormData();
  const { channel = {}, ...inboxProperties } = inboxParams;
  Object.keys(inboxProperties).forEach(key => {
    formData.append(key, inboxProperties[key]);
  });
  const { selectedFeatureFlags, ...channelParams } = channel;
  // selectedFeatureFlags needs to be empty when creating a website channel
  if (selectedFeatureFlags) {
    if (selectedFeatureFlags.length) {
      selectedFeatureFlags.forEach(featureFlag => {
        formData.append(`channel[selected_feature_flags][]`, featureFlag);
      });
    } else {
      formData.append('channel[selected_feature_flags][]', '');
    }
  }
  Object.keys(channelParams).forEach(key => {
    formData.append(`channel[${key}]`, channel[key]);
  });
  return formData;
};

export const state = {
  meta: {
    count: 0,
    currentPage: 1,
  },
  records: [],
  uiFlags: {
    isFetching: false,
    isFetchingQualityHistory: false,
    isFetchingBusinessManager: false,
    isFetchingTemplateSendStatus: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isUpdatingIMAP: false,
    isUpdatingSMTP: false,
  },
};

export const getters = {
  getInboxes($state) {
    return $state.records;
  },
  getWhatsAppTemplates: $state => inboxId => {
    const [inbox] = $state.records.filter(
      record => record.id === Number(inboxId)
    );

    const {
      message_templates: whatsAppMessageTemplates,
      additional_attributes: additionalAttributes,
    } = inbox || {};

    const { message_templates: apiInboxMessageTemplates } =
      additionalAttributes || {};
    const messagesTemplates =
      whatsAppMessageTemplates || apiInboxMessageTemplates;

    if (messagesTemplates instanceof Array) {
      const templates = messagesTemplates.filter(({ name, status }) => {
        //Removendo templates de exemplo
        return (!name.startsWith("sample") && status.toLowerCase() === "approved")
      });

      return templates
    }
    return [];
  },
  getNewConversationInboxes($state) {
    return $state.records.filter(inbox => {
      const {
        channel_type: channelType,
        phone_number: phoneNumber = '',
      } = inbox;

      const isEmailChannel = channelType === INBOX_TYPES.EMAIL;
      const isSmsChannel =
        channelType === INBOX_TYPES.TWILIO &&
        phoneNumber.startsWith('whatsapp');
      return isEmailChannel || isSmsChannel;
    });
  },
  getInbox: $state => inboxId => {
    const [inbox] = $state.records.filter(
      record => record.id === Number(inboxId)
    );
    return inbox || {};
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getWebsiteInboxes($state) {
    return $state.records.filter(item => item.channel_type === INBOX_TYPES.WEB);
  },
  getWhatsAppInboxes($state) {
    return $state.records.filter(
        item =>
          (item.channel_type === INBOX_TYPES.WHATSAPP) ||
          (item?.additional_attributes?.provider === "wppconnect")
    );
  },
  getTwilioInboxes($state) {
    return $state.records.filter(
      item => item.channel_type === INBOX_TYPES.TWILIO
    );
  },
  getSMSInboxes($state) {
    return $state.records.filter(
      item =>
        item.channel_type === INBOX_TYPES.SMS ||
        (item.channel_type === INBOX_TYPES.TWILIO && item.medium === 'sms')
    );
  },
  dialogFlowEnabledInboxes($state) {
    return $state.records.filter(
      item => item.channel_type !== INBOX_TYPES.EMAIL
    );
  },
  allowCalls: $state => inboxId => {
    const [inbox] = $state.records.filter(
      record => record.id === Number(inboxId)
    );
    return (inbox && inbox.channel_type == 'Channel::Whatsapp');
  },
};

const sendAnalyticsEvent = channelType => {
  AnalyticsHelper.track(ACCOUNT_EVENTS.ADDED_AN_INBOX, {
    channelType,
  });
};

export const actions = {
  revalidate: async ({ commit }, { newKey }) => {
    try {
      const isExistingKeyValid = await InboxesAPI.validateCacheKey(newKey);
      if (!isExistingKeyValid) {
        const response = await InboxesAPI.refetchAndCommit(newKey);
        commit(types.default.SET_INBOXES, response.data.payload);
      }
    } catch (error) {
      // Ignore error
    }
  },
  get: async ({ commit }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: true });
    try {
      const response = await InboxesAPI.get(true);
      commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: false });
      commit(types.default.SET_INBOXES, response.data.payload);
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: false });
    }
  },
  getVollTabulations: async (_, { domain, api_key }) => {
    try {
      return await Voll360.getTabulations(domain, api_key);
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    }
  },
  getVollAgents: async (_, { domain, api_key }) => {
    try {
      return await Voll360.getAgents(domain, api_key);
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    }
  },
  getVollCampaigns: async (_, { domain, api_key }) => {
    try {
      return await Voll360.getCampaigns(domain, api_key);
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    }
  },
  getWetalkieTabulations: async (_, { domain, username, password }) => {
    try {
        return await Wetalkie.getTabulations(domain, username, password);
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    }
  },
  getWetalkieTabulationsByInbox: async (_, { identifier }) => {
    try {
        return await Wetalkie.getTabulationsByInbox(identifier);
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    }
  },
  getWetalkieUsers: async (_, { domain, username, password }) => {
    try {
        return await Wetalkie.getUsers(domain, username, password);
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    }
  },
  getWetalkieUsersByInbox: async (_, { identifier }) => {
    try {
        return await Wetalkie.getUsersByInbox(identifier);
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    }
  },
  fetchWetalkieConversations: async (_, { identifier, beginDate, endDate, force }) => {
    try {
      return await Wetalkie.fetchConversations(identifier, {
        beginDate,
        endDate,
        force,
      });
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    }
  },
  pauseInboxIngestion: async (_, { inboxId, pauseUntil }) => {
    try {
      const response = await InboxesAPI.pauseIngestion(inboxId, pauseUntil);
      return response.data;
    } catch (error) {
      const errorMessage = error?.response?.data?.error;
      throw new Error(errorMessage);
    }
  },
  fetchInfobipConversations: async (
    _,
    { identifier, beginDate, endDate, force, updateExisting }
  ) => {
    try {
      return await Infobip.fetchConversations(identifier, {
        beginDate,
        endDate,
        force,
        updateExisting,
      });
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    }
  },
  fetchZendeskConversations: async (_, { identifier, beginDate, endDate, force }) => {
    try {
      return await Zendesk.fetchConversations(identifier, {
        beginDate,
        endDate,
        force,
      });
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    }
  },
  getZendeskUsers: async (_, { subdomain, email, api_token }) => {
    try {
      return await Zendesk.getUsers(subdomain, email, api_token);
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    }
  },
  getZendeskGroups: async (_, { subdomain, email, api_token }) => {
    try {
      return await Zendesk.getGroups(subdomain, email, api_token);
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    }
  },
  getZendeskTags: async (_, { subdomain, email, api_token }) => {
    try {
      return await Zendesk.getTags(subdomain, email, api_token);
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    }
  },
  getInfobipAgents: async (_, { baseUrl, apiKey }) => {
    try {
      return await Infobip.getAgentsByCredentials(baseUrl, apiKey);
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    }
  },
  getInfobipQueues: async (_, { baseUrl, apiKey }) => {
    try {
      return await Infobip.getQueuesByCredentials(baseUrl, apiKey);
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    }
  },
  getInfobipTags: async (_, { baseUrl, apiKey }) => {
    try {
      return await Infobip.getTagsByCredentials(baseUrl, apiKey);
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    }
  },
  getInfobipAgentsByInbox: async (_, { inboxId, params }) => {
    try {
      return await Infobip.getAgents(inboxId, params);
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    }
  },
  getInfobipQueuesByInbox: async (_, { inboxId, params }) => {
    try {
      return await Infobip.getQueues(inboxId, params);
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    }
  },
  getInfobipTagsByInbox: async (_, { inboxId, params }) => {
    try {
      return await Infobip.getTags(inboxId, params);
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    }
  },
  getMultimediaCredentials: async (_, { inboxId }) => {
    try {
      const response = await InboxesAPI.getMultimediaCredentials(inboxId);
      return response.data;
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    }
  },
  getQualityHistory: async ({ commit }, { inboxId }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isFetchingQualityHistory: true });
    try {
      const response = await InboxesAPI.getQualityHistory(inboxId);
      return response.data;
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    } finally {
      commit(types.default.SET_INBOXES_UI_FLAG, {
        isFetchingQualityHistory: false,
      });
    }
  },
  getTemplateSendStatus: async ({ commit }, { inboxId }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, {
      isFetchingTemplateSendStatus: true,
    });
    try {
      const response = await InboxesAPI.getTemplateSendStatus(inboxId);
      return response.data;
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    } finally {
      commit(types.default.SET_INBOXES_UI_FLAG, {
        isFetchingTemplateSendStatus: false,
      });
    }
  },
  getBusinessManager: async ({ commit }, { inboxId }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isFetchingBusinessManager: true });
    try {
      const response = await InboxesAPI.getBusinessManager(inboxId);
      return response.data;
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message;
      throw new Error(errorMessage || '');
    } finally {
      commit(types.default.SET_INBOXES_UI_FLAG, {
        isFetchingBusinessManager: false,
      });
    }
  },
  createChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await WebChannel.create(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      const { channel = {} } = params;
      sendAnalyticsEvent(channel.type);
      return response.data;
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw new Error(errorMessage);
    }
  },
  createWebsiteChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await WebChannel.create(buildInboxData(params));
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('website');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      return throwErrorMessage(error);
    }
  },
  createTwilioChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await TwilioChannel.create(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('twilio');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw new Error(error);
    }
  },
  createFBChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await FBChannel.create(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('facebook');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw new Error(error);
    }
  },
  createWhatsAppEmbeddedSignup: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await WhatsappChannel.createEmbeddedSignup(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('whatsapp');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw error;
    }
  },
  updateInbox: async ({ commit }, { id, formData = true, ...inboxParams }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: true });
    try {
      const response = await InboxesAPI.update(
        id,
        formData ? buildInboxData(inboxParams) : inboxParams
      );
      commit(types.default.EDIT_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: false });
      throwErrorMessage(error);
    }
  },
  updateInboxIMAP: async ({ commit }, { id, ...inboxParams }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingIMAP: true });
    try {
      const response = await InboxesAPI.update(id, inboxParams);
      commit(types.default.EDIT_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingIMAP: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingIMAP: false });
      throwErrorMessage(error);
    }
  },
  updateInboxSMTP: async ({ commit }, { id, ...inboxParams }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingSMTP: true });
    try {
      const response = await InboxesAPI.update(id, inboxParams);
      commit(types.default.EDIT_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingSMTP: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingSMTP: false });
      throwErrorMessage(error);
    }
  },
  delete: async ({ commit }, inboxId) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isDeleting: true });
    try {
      await InboxesAPI.delete(inboxId);
      commit(types.default.DELETE_INBOXES, inboxId);
      commit(types.default.SET_INBOXES_UI_FLAG, { isDeleting: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isDeleting: false });
      throw new Error(error);
    }
  },
  toggleStatus: async ({ commit }, { accountId, inboxId, isActive }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: true });

    try {
      await InboxesAPI.toggleStatus(accountId, inboxId, isActive);

      commit(types.default.EDIT_INBOXES, {
        id: inboxId,
        is_inbox_active: isActive,
      });

      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: false });
    } catch (error) {

      if (error?.response?.status >= 400) {
        bus.$emit('newToastMessage', 'Erro ao atualizar inbox');
      } else {
        console.warn('Ignorando erro não crítico');
      }
    }
  },
  reauthorizeFacebookPage: async ({ commit }, params) => {
    try {
      const response = await FBChannel.reauthorizeFacebookPage(params);
      commit(types.default.EDIT_INBOXES, response.data);
    } catch (error) {
      throw new Error(error.message);
    }
  },
  deleteInboxAvatar: async (_, inboxId) => {
    try {
      await InboxesAPI.deleteInboxAvatar(inboxId);
    } catch (error) {
      throw new Error(error);
    }
  },
  search: async ({ commit }, { search, page, limit = 20, sortAttr, include_subaccounts = false }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: true });
    try {
      const {
        data: { payload, meta },
      } = await InboxesAPI.search(search, page, limit, sortAttr, include_subaccounts);      
      commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: false });
      commit(types.default.SET_INBOXES_META, meta);
      commit(types.default.SET_INBOXES, payload);
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: false });
    }
  },
  setInboxes({ commit }, inboxes) {
    commit(types.default.SET_INBOXES, inboxes);
  },
};

export const mutations = {
  [types.default.SET_INBOXES_UI_FLAG]($state, uiFlag) {
    $state.uiFlags = { ...$state.uiFlags, ...uiFlag };
  },
  [types.default.SET_INBOXES]: MutationHelpers.set,
  [types.default.SET_INBOXES_ITEM]: MutationHelpers.setSingleRecord,
  [types.default.ADD_INBOXES]: MutationHelpers.create,
  [types.default.EDIT_INBOXES]: MutationHelpers.update,
  [types.default.DELETE_INBOXES]: MutationHelpers.destroy,
  [types.default.SET_INBOXES_META]: ($state, data) => {
    const { count, current_page: currentPage } = data;
    $state.meta.count = count;
    $state.meta.currentPage = currentPage;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
